import { QUOTATION_API_URL } from "../../../../config";
import { LogCategory } from "../../../../config/logging";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { buildQuotationApiHeaders } from "./quotationApiHeaders";

export interface CreateOrcamentoFromQuotationInput {
	clientId: number;
	userId: number;
	productId: number;
	productName: string;
	produtoType: number;
	materialTypeId?: number | null;
	unitId: number;
	quantity: number;
	unitPrice: number;
	totalValue: number;
	discountValue?: number;
	observacaoPedido?: string;
	observacaoItem?: string;
	exibirRecibo?: boolean;
	formaPagamentoId?: number | null;
}

export interface CreateOrcamentoFromQuotationResult {
	orcId: number;
	oitId: number | null;
	raw: any;
}

export interface GeneratePixQrCodeInput {
	orcId: number;
	userId: number;
	clientId: number;
	totalValue: number;
	clientName: string;
	clientDocument?: string | null;
}

export interface GeneratePixQrCodeResult {
	txid: string;
	brcode: string;
	idPagamento?: number;
	raw: any;
}

function getQuotationApiBaseUrl(): string {
	const base = String(QUOTATION_API_URL || "").trim();
	if (!base || !base.startsWith("http")) {
		throw new Error("Base da API de orçamento inválida para create-orcamento/PIX.");
	}
	const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
	sendToTerminal(
		"🧭 [QUOTATION] Base resolvida para orçamento/PIX",
		{
			baseUrl: normalizedBase,
			source:
				import.meta.env.DEV
					? "dev -> QUOTATION_API_URL resolvida pelo config"
					: "production -> QUOTATION_API_URL/env",
		},
		LogCategory.API,
	);
	return normalizedBase;
}

async function parseJsonSafe(response: Response): Promise<any> {
	try {
		return await response.json();
	} catch {
		return {};
	}
}

function parseErrorMessage(data: any, fallback: string): string {
	return (
		data?.message ||
		data?.error ||
		data?.msg ||
		data?.data?.message ||
		data?.data?.error ||
		fallback
	);
}

function buildDetailedHttpError(
	response: Response,
	apiMessage: string,
): string {
	return `HTTP ${response.status} ${response.statusText} - ${apiMessage}`;
}

function normalizeMoneyString(value: number): string {
	return Number.isFinite(value) ? value.toFixed(2) : "0.00";
}

function normalizeDocument(documentRaw: string | null | undefined): {
	cpf?: string;
	cnpj?: string;
} {
	const digits = String(documentRaw || "").replace(/\D/g, "");
	if (digits.length === 11) return { cpf: digits };
	if (digits.length === 14) return { cnpj: digits };
	return {};
}

export async function createOrcamentoFromQuotation(
	input: CreateOrcamentoFromQuotationInput,
): Promise<CreateOrcamentoFromQuotationResult> {
	const apiBaseUrl = getQuotationApiBaseUrl();
	const endpoint = `${apiBaseUrl}/create-orcamento`;
	const headers = buildQuotationApiHeaders();

	const quantity =
		input.quantity > 0
			? Number(input.quantity.toFixed(2))
			: Number(input.quantity);
	const unitPrice = Number(input.unitPrice.toFixed(2));
	const totalValue = Number(input.totalValue.toFixed(2));
	const discountValue = Number((input.discountValue ?? 0).toFixed(2));
	const materialTypeId = Number.isFinite(input.materialTypeId ?? NaN)
		? Number(input.materialTypeId)
		: 0;

	const payload = {
		client: input.clientId,
		formaPagamento: input.formaPagamentoId ?? 11,
		status: 1,
		valorFrete: "0.00",
		valorTotal: normalizeMoneyString(totalValue),
		orc_valor_discount: normalizeMoneyString(discountValue),
		usuariocadastro: input.userId,
		flag: "externo",
		orc_type_discount: 0,
		exibirRecibo: input.exibirRecibo ?? false,
		orc_observacao:
			input.observacaoPedido ||
			"Pedido criado via pipeline de orcamento (CorelDRAW Front-End)",
		produto_orcamento: [
			{
				pro_id: input.productId,
				pro_nome: input.productName,
				pro_desconto: "0",
				qnty: quantity,
				type: materialTypeId,
				produto_type: input.produtoType || 1,
				pro_discount_type: 0,
				prod_und_id: input.unitId,
				produto_preco_unidade: {
					pro_venda: unitPrice,
				},
				pro_visible: "separacao",
				content: input.observacaoItem || "",
				oit_id: null,
				prod_deleted: false,
				is_personalizado: false,
			},
		],
	};

	sendToTerminal(
		"📦 [QUOTATION] Registrando orcamento via API",
		{
			endpoint,
			clientId: input.clientId,
			productId: input.productId,
			materialTypeId,
			quantity,
			unitPrice,
			totalValue,
			hasAuthorization: !!headers.Authorization,
			tenantId: headers["X-Tenant-Id"] || "(não enviado)",
			//appRegion: headers["x-app-region"] || "(não enviada)",
		},
		LogCategory.BUSINESS,
	);

	const response = await fetch(endpoint, {
		method: "POST",
		headers,
		body: JSON.stringify(payload),
		mode: "cors",
	});

	const data = await parseJsonSafe(response);

	if (!response.ok) {
		const apiMessage = parseErrorMessage(data, "Erro na requisicao");
		sendToTerminal(
			"❌ [QUOTATION] Falha HTTP ao registrar orcamento",
			{
				endpoint,
				status: response.status,
				statusText: response.statusText,
				message: apiMessage,
				responseData: data,
			},
			LogCategory.ERROR,
		);
		throw new Error(buildDetailedHttpError(response, apiMessage));
	}

	const orcIdRaw = data?.pedido?.id ?? data?.data?.pedido?.id ?? data?.id;
	const primaryOitIdRaw = data?.pedido?.oit_id ?? data?.data?.pedido?.oit_id;
	const oitIds = data?.pedido?.oit_ids ?? data?.data?.pedido?.oit_ids;
	const oitIdRaw =
		primaryOitIdRaw ?? (Array.isArray(oitIds) ? oitIds[0] : undefined);

	const orcId = Number(orcIdRaw);
	const parsedOitId = Number(oitIdRaw);
	const oitId =
		Number.isFinite(parsedOitId) && parsedOitId > 0 ? parsedOitId : null;

	if (!Number.isFinite(orcId) || orcId <= 0) {
		throw new Error("API create-orcamento retornou orc_id invalido.");
	}

	sendToTerminal(
		"✅ [QUOTATION] Orcamento registrado",
		{
			orcId,
			oitId,
			hasOitId: oitId !== null,
			responseKeys: Object.keys(data || {}),
		},
		LogCategory.BUSINESS,
	);

	return {
		orcId,
		oitId,
		raw: data,
	};
}

export async function generatePixQrCode(
	input: GeneratePixQrCodeInput,
): Promise<GeneratePixQrCodeResult> {
	const apiBaseUrl = getQuotationApiBaseUrl();
	const endpoint = `${apiBaseUrl}/pix/qrCode-generate`;
	const headers = buildQuotationApiHeaders();
	const totalValue = Number(input.totalValue.toFixed(2));
	const document = normalizeDocument(input.clientDocument);

	const payload = {
		id_orc: input.orcId,
		id_usuario: input.userId,
		id_client: input.clientId,
		valor: totalValue,
		valorTotal: totalValue,
		nome: input.clientName,
		ost_id: 1,
		...document,
	};

	sendToTerminal(
		"💳 [QUOTATION] Gerando PIX do pedido",
		{
			endpoint,
			orcId: input.orcId,
			clientId: input.clientId,
			totalValue,
			hasCpf: !!document.cpf,
			hasCnpj: !!document.cnpj,
			hasAuthorization: !!headers.Authorization,
			tenantId: headers["X-Tenant-Id"] || "(não enviado)",
			//appRegion: headers["x-app-region"] || "(não enviada)",
		},
		LogCategory.BUSINESS,
	);

	const response = await fetch(endpoint, {
		method: "POST",
		headers,
		body: JSON.stringify(payload),
		mode: "cors",
	});

	const data = await parseJsonSafe(response);

	if (!response.ok) {
		const apiMessage = parseErrorMessage(data, "Erro na requisicao");
		sendToTerminal(
			"❌ [QUOTATION] Falha HTTP ao gerar PIX",
			{
				endpoint,
				status: response.status,
				statusText: response.statusText,
				message: apiMessage,
				responseData: data,
			},
			LogCategory.ERROR,
		);
		throw new Error(buildDetailedHttpError(response, apiMessage));
	}

	const txid = String(data?.pix?.txid || data?.txid || "");
	const brcode = String(data?.pix?.brcode || data?.brcode || "");
	const idPagamento = Number(data?.createPix?.id_pagamento);

	if (!txid || !brcode) {
		throw new Error("API PIX nao retornou txid/brcode validos.");
	}

	sendToTerminal(
		"✅ [QUOTATION] PIX retornado pela API",
		{
			orcId: input.orcId,
			txid,
			idPagamento: Number.isFinite(idPagamento) ? idPagamento : null,
			brcodeLength: brcode.length,
		},
		LogCategory.BUSINESS,
	);

	return {
		txid,
		brcode,
		idPagamento: Number.isFinite(idPagamento) ? idPagamento : undefined,
		raw: data,
	};
}
